/**
 * @file
 * The remove-confirmation popover (berTags.confirm).
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { el, textSpan, onClick } = berTags.dom;

  // A little confirmation popover anchored at a remove control. One at a
  // time; Escape, Cancel or an outside press dismisses without removing.
  const confirmPopover = (() => {
    let bubble = null;
    let teardown = null;
    let portalEl = null;
    // The same portal contract as the balloons (see berTags.popover): the
    // anchor class keeps the outside-press closer from treating presses
    // inside the bubble as outside, ber-tags skins the controls, __portal
    // carries stacking. The confirm's own modifier lifts it above open
    // balloons, whatever the portals' DOM order.
    const portal = () => {
      if (!portalEl) {
        portalEl = el(
          'span',
          'ber-tags__anchor ber-tags ber-upload__portal ber-confirm__portal',
        );
        document.body.appendChild(portalEl);
      }
      return portalEl;
    };
    const close = (refocus) => {
      if (teardown) {
        teardown();
        teardown = null;
      }
      if (bubble) {
        const anchor = bubble.berAnchor;
        bubble.remove();
        bubble = null;
        if (anchor) {
          anchor.classList.remove('is-pop-anchor');
        }
        if (refocus && anchor && anchor.isConnected) {
          anchor.focus();
        }
      }
    };
    // message: a string, or { message, hint, yes } where hint is a muted
    // clarification line under the question and yes relabels the confirm
    // button (default "Remove").
    const open = (anchor, message, onConfirm) => {
      const {
        message: title,
        hint = '',
        yes: yesLabel = '',
      } = typeof message === 'string' ? { message } : message;
      close(false);
      berTags.tooltip.hide();
      bubble = el('div', 'ber-confirm');
      bubble.setAttribute('role', 'alertdialog');
      bubble.setAttribute('aria-label', hint ? `${title} ${hint}` : title);
      bubble.berAnchor = anchor;
      // Keeps the anchor's tile in its hover state while the bubble is up.
      anchor.classList.add('is-pop-anchor');
      const text = textSpan('ber-confirm__text', title);
      const yes = el('button', 'ber-confirm__yes');
      yes.type = 'button';
      yes.textContent = yesLabel || Drupal.t('Remove');
      const no = el('button', 'ber-confirm__no');
      no.type = 'button';
      no.textContent = Drupal.t('Cancel');
      const row = el('div', 'ber-confirm__actions');
      row.append(yes, no);
      bubble.append(text);
      if (hint) {
        bubble.append(textSpan('ber-confirm__hint', hint));
      }
      bubble.append(row);
      portal().appendChild(bubble);

      // The shared coordinate anchoring: page-pinned wrapper, viewport
      // clamp, below/above flip and scroll/resize re-evaluation all live
      // in popover.anchorAt.
      const anchored = berTags.popover.anchorAt(anchor, bubble, {
        wrap: portal(),
        onFlip: (below) => bubble.classList.toggle('ber-confirm--below', below),
        onDetach: () => close(false),
      });

      const onDown = (event) => {
        if (bubble && !bubble.contains(event.target)) {
          close(false);
        }
      };
      const onKey = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close(true);
        }
      };
      document.addEventListener('pointerdown', onDown, true);
      document.addEventListener('keydown', onKey, true);
      teardown = () => {
        anchored.dispose();
        document.removeEventListener('pointerdown', onDown, true);
        document.removeEventListener('keydown', onKey, true);
      };
      onClick(yes, () => {
        close(false);
        onConfirm();
      });
      onClick(no, () => close(true));
      yes.focus();
    };
    return { open, close };
  })();

  berTags.confirm = confirmPopover;
})(Drupal);
